import React, { useCallback, useEffect, useMemo, useState } from "react"
import moment from "moment"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"
import { apiWorkspaceBookingAvailableDates } from "~/routes"

import DateSelector from "~/library/DateSelector"

type AvailableDateRow = { date: string }

const BookingAvailableDatesCalendar: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()

    const [isLoading, setIsLoading] = useState(false)
    const [dates, setDates] = useState<Set<string>>(new Set())

    const fetchDates = useCallback(async () => {
        setIsLoading(true)
        try {
            const url = apiWorkspaceBookingAvailableDates.index.path({ venueSlug })
            const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" } })
            if (!res.ok) throw new Error(`Failed to load (${res.status})`)

            const body = await res.json()
            const rows: AvailableDateRow[] = body.booking_available_dates || []

            const next = new Set<string>()
            rows.forEach((r) => {
                if (r?.date) next.add(moment(r.date).format("YYYY-MM-DD"))
            })
            setDates(next)
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
            toast("Error loading available dates", "danger")
        } finally {
            setIsLoading(false)
        }
    }, [toast, venueSlug])

    useEffect(() => {
        fetchDates()
    }, [fetchDates])

    const activeDates = useMemo(() => Array.from(dates), [dates])

    const toggleDate = useCallback(
        async (d: moment.Moment, next: boolean) => {
            const key = d.format("YYYY-MM-DD")

            // optimistic
            setDates((prev) => {
                const s = new Set(prev)
                if (next) s.add(key)
                else s.delete(key)
                return s
            })

            try {
                if (next) {
                    await apiWorkspaceBookingAvailableDates.create({
                        data: { venue_slug: venueSlug, booking_available_date: { date: key } },
                    })
                } else {
                    await apiWorkspaceBookingAvailableDates.destroy({ venue_slug: venueSlug, id: key })
                }
            } catch (e) {
                // rollback
                setDates((prev) => {
                    const s = new Set(prev)
                    if (next) s.delete(key)
                    else s.add(key)
                    return s
                })
                // eslint-disable-next-line no-console
                console.error(e)
                toast("Failed to save date", "danger")
            }
        },
        [toast, venueSlug]
    )

    if (isLoading) return <LoadingWrapper isLoading />

    return (
        <div className="booking-calendar-container">
            <PageTitle title="Available Dates" />

            <div className="card">
                <div className="card-body">
                    <DateSelector
                        mode="toggle"
                        dateFormat="YYYY-MM-DD"
                        activeDates={activeDates}
                        disabledDates={[]}
                        futureOnly={false}
                        onToggle={toggleDate}
                    />

                    <div className="text-muted mt-3">
                        <small>Click dates to enable/disable booking.</small>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BookingAvailableDatesCalendar
